import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import { useUser } from 'src/providers/UserProvider'

import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Note } from 'src/components/atoms/Note'
import { Page } from 'src/components/layouts/Page'
import { Table } from 'src/components/molecules/Table'
import { AccountUsersActions } from 'src/components/organisms/actions/AccountUsersActions'
import {
  AccountNav,
  AccountNavTab
} from 'src/components/organisms/navs/AccountNav'
import { VStack } from 'src/components/templates/VStack'

import { useListAccountMembershipsAndInvitesQuery } from 'src/config/generated/graphql'
import useMediaQuery from 'src/hooks/useMediaQuery'
import { MembershipRoleType } from 'src/types/account'
import { withAuthRequired } from 'src/utils/auth'
import { formatDate } from 'src/utils/date'

type AccountUsersType = {
  UserId?: string
  Name: string
  Email: string
  Type: string
  Role?: MembershipRoleType
  CreatedOn: string
  InvitationId?: string
  MembershipId?: string
  Status?: string
  MembershipOwnerCount?: number
}

const InviteStatusLabel = ({ status }: { status: string }) => (
  <span className='capitalize ml-[10px] px-[10px] py-2 bg-[#F5F5F5] text-mostlyGreen text-[14px] leading-[10px] font-semibold'>
    {status}
  </span>
)

const ViewAccountUsers = () => {
  const { user, isAccountOwner } = useUser()
  const { data, loading } = useListAccountMembershipsAndInvitesQuery({
    variables: {
      accountId: user?.Account?.Id,
      inviteExpiry: dayjs().utc(true).toString()
    }
  })
  const isMobile = useMediaQuery('(max-width: 1024px)')

  const columnHelper = createColumnHelper<AccountUsersType>()

  const desktopColumns: ColumnDef<AccountUsersType, any>[] = [
    columnHelper.accessor('Name', {
      header: 'Name',
      cell: props => (
        <span>
          {props.getValue()}
          {props.row.original.UserId === user?.Id ? ' (You)' : null}
          {props.row.original.InvitationId ? (
            <InviteStatusLabel status={props.row.original.Status!} />
          ) : null}
        </span>
      )
    }),
    columnHelper.accessor('Email', {
      header: 'Email',
      size: 180,
      cell: props => <span>{props.getValue()}</span>
    }),
    columnHelper.accessor('Type', {
      header: 'Role',
      cell: props => <span>{props.getValue()}</span>,
      size: 60
    }),
    columnHelper.accessor('CreatedOn', {
      header: 'Added on',
      size: 90,
      cell: props => (
        <span>
          {`${
            props.row.original.InvitationId ? 'Invitation sent on' : ''
          } ${formatDate({
            date: props.row.original.CreatedOn,
            format: 'DD MMM YYYY',
            isUtc: true
          })}`}
        </span>
      )
    }),
    isAccountOwner
      ? columnHelper.accessor('UserId', {
          header: 'Action',
          size: 80,
          cell: ({ row: { original } }) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div onClick={e => e.stopPropagation()}>
              <AccountUsersActions
                userId={original.UserId}
                invitationId={original.InvitationId}
                membershipId={original.MembershipId}
                role={original.Role}
                accountId={user?.Account?.Id}
                membershipOwnersCount={original.MembershipOwnerCount}
              />
            </div>
          )
        })
      : (null as any)
  ].filter(Boolean)

  const mobileColumns: ColumnDef<AccountUsersType, any>[] = [
    columnHelper.accessor('UserId', {
      header: '',
      cell: ({ row: { original } }) => (
        <div className='flex flex-col text-left'>
          <dl className='mb-3 pb-2'>
            <DefinitionListItem
              title='Name'
              data={
                <>
                  {original.Name}
                  {original.InvitationId ? (
                    <InviteStatusLabel status={original.Status!} />
                  ) : null}
                </>
              }
            />
            <DefinitionListItem title='Email' data={original.Email} />
            <DefinitionListItem title='Role' data={original.Type} />
            <DefinitionListItem
              title='Added on'
              data={`${
                original.InvitationId ? 'Invitation sent on' : ''
              } ${formatDate({
                date: original.CreatedOn,
                format: 'DD MMM YYYY',
                isUtc: true
              })}`}
            />
            <DefinitionListItem
              title='Actions'
              data={
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div onClick={e => e.stopPropagation()}>
                  <AccountUsersActions
                    userId={original.UserId}
                    invitationId={original.InvitationId}
                    membershipId={original.MembershipId}
                    role={original.Role}
                    accountId={user?.Account?.Id}
                    membershipOwnersCount={original.MembershipOwnerCount}
                  />
                </div>
              }
            />
          </dl>
        </div>
      )
    })
  ]

  const userData = useMemo<Array<AccountUsersType>>(() => {
    return (data?.Invite || []).map(i => ({
      CreatedOn: i.CreatedOn,
      Email: i.RecipientEmail,
      UserId: undefined,
      Name: `${i.RecipientFirstname} ${i.RecipientLastname}`,
      Type: 'Member',
      InvitationId: i.Id,
      MembershipId: undefined,
      Status: i.Status ? i.Status : 'Pending',
      MembershipOwnerCount: 0
    }))
  }, [data]).concat(
    (data?.Membership || []).map(m => ({
      CreatedOn: m.User.CreatedOn,
      Email: m.User.Email,
      UserId: m.User.Id,
      Name: `${m.User.FirstName} ${m.User.LastName}`,
      Type: MembershipRoleType[m.Type] as string,
      InvitationId: undefined,
      MembershipId: m.Id,
      Role: m.Type,
      Status: undefined,
      MembershipOwnerCount: m.Account.Memberships.filter(
        am => am.Type === MembershipRoleType.Owner
      ).length
    }))
  )

  return (
    <VStack>
      <LoadingSpinner isLoading={loading}>
        <AccountNav activeTab={AccountNavTab.Users}>
          <div className='mb-5 md:mb-10'>
            {data?.Membership.length === 1 ? (
              <Note className='mt-5 md:mt-10' type='info'>
                You can now invite users to your account. Owners can add or
                remove members from this account.
              </Note>
            ) : null}
            <Table
              className='mt-0'
              enableSorting={false}
              headerClasses={accessor =>
                accessor === 'User_Id' ? '!text-left' : ''
              }
              cellClasses={accessor =>
                accessor === 'User_Id' ? '!text-left' : ''
              }
              columns={isMobile ? mobileColumns : desktopColumns}
              data={userData}
            />
          </div>
        </AccountNav>
      </LoadingSpinner>
    </VStack>
  )
}

const ViewAccountUsersPage = () => {
  return (
    <Page showBreadcrumbs={false}>
      <ViewAccountUsers />
    </Page>
  )
}

export default withAuthRequired(ViewAccountUsersPage)
